import { logger } from '@/core/logger';
import * as ImageManipulator from 'expo-image-manipulator';

/**
 * Frame dimensions from Figma design
 * Position: (24, 304)
 * Size: 345x244
 */
const FRAME_X = 24;
const FRAME_Y = 304;
const FRAME_WIDTH = 345;
const FRAME_HEIGHT = 244;

export interface CropRegion {
    originX: number;
    originY: number;
    width: number;
    height: number;
}

/**
 * Calculate crop region based on frame position and screen dimensions
 * The frame position is relative to a 393px wide portrait screen (iPhone design)
 * Handles both portrait and landscape orientations
 * We need to scale it to the actual image dimensions
 */
function calculateCropRegion(
    imageWidth: number,
    imageHeight: number,
    screenWidth: number = 393,
    screenHeight: number = 852
): CropRegion {
    // Detect if screen is in landscape mode (width > height)
    const isScreenLandscape = screenWidth > screenHeight;
    const isImageLandscape = imageWidth > imageHeight;

    logger.info('calculateCropRegion', 'Orientation detection', {
        isScreenLandscape,
        isImageLandscape,
        screenWidth,
        screenHeight,
        imageWidth,
        imageHeight,
    });

    let effectiveScreenWidth = screenWidth;
    let effectiveScreenHeight = screenHeight;
    let frameX = FRAME_X;
    let frameY = FRAME_Y;
    let frameWidth = FRAME_WIDTH;
    let frameHeight = FRAME_HEIGHT;

    // If screen is landscape, adjust frame coordinates
    // Frame is designed for portrait (393x852), so when landscape we need to adjust
    if (isScreenLandscape) {
        // In landscape, screen dimensions are swapped (width > height)
        // Frame position needs to be adjusted:
        // - Frame X (24) in portrait becomes Frame Y in landscape
        // - Frame Y (304) in portrait becomes Frame X in landscape
        // - Frame width (345) in portrait becomes Frame height in landscape
        // - Frame height (244) in portrait becomes Frame width in landscape
        
        // Swap frame coordinates for landscape
        frameX = FRAME_Y; // 304 (was Y in portrait)
        frameY = FRAME_X; // 24 (was X in portrait)
        frameWidth = FRAME_HEIGHT; // 244 (was height in portrait)
        frameHeight = FRAME_WIDTH; // 345 (was width in portrait)
        
        // Use swapped screen dimensions for scaling
        effectiveScreenWidth = screenHeight; // Use height as width reference
        effectiveScreenHeight = screenWidth; // Use width as height reference

        logger.info('calculateCropRegion', 'Landscape mode detected - adjusting frame coordinates', {
            originalFrame: { x: FRAME_X, y: FRAME_Y, width: FRAME_WIDTH, height: FRAME_HEIGHT },
            adjustedFrame: { x: frameX, y: frameY, width: frameWidth, height: frameHeight },
            effectiveScreen: { width: effectiveScreenWidth, height: effectiveScreenHeight },
        });
    }

    // Calculate scale factors based on effective screen dimensions
    const scaleX = imageWidth / effectiveScreenWidth;
    const scaleY = imageHeight / effectiveScreenHeight;

    // Scale frame dimensions to image dimensions
    const originX = frameX * scaleX;
    const originY = frameY * scaleY;
    const width = frameWidth * scaleX;
    const height = frameHeight * scaleY;

    // Ensure crop region is within image bounds
    const clampedOriginX = Math.max(0, Math.min(originX, imageWidth - width));
    const clampedOriginY = Math.max(0, Math.min(originY, imageHeight - height));
    const clampedWidth = Math.min(width, imageWidth - clampedOriginX);
    const clampedHeight = Math.min(height, imageHeight - clampedOriginY);

    logger.info('calculateCropRegion', 'Crop region calculated', {
        originX: clampedOriginX,
        originY: clampedOriginY,
        width: clampedWidth,
        height: clampedHeight,
        scaleX,
        scaleY,
    });

    return {
        originX: clampedOriginX,
        originY: clampedOriginY,
        width: clampedWidth,
        height: clampedHeight,
    };
}

/**
 * Auto-crop an image based on the document frame position
 * Keeps image as captured (no rotation) regardless of phone orientation
 * @param imageUri - URI of the image to crop
 * @param imageWidth - Width of the captured image
 * @param imageHeight - Height of the captured image
 * @param screenWidth - Width of the screen (default: 393 for iPhone design)
 * @param screenHeight - Height of the screen (default: 852 for iPhone design)
 * @returns URI of the cropped image
 */
export async function autoCropDocument(
    imageUri: string,
    imageWidth: number,
    imageHeight: number,
    screenWidth: number = 393,
    screenHeight: number = 852
): Promise<string> {
    try {
        logger.info('autoCropDocument', 'Starting auto-crop', {
            imageUri,
            imageWidth,
            imageHeight,
            screenWidth,
            screenHeight,
        });

        // Calculate crop region directly based on actual image dimensions
        // No rotation - keep image as captured (portrait or landscape)
        const cropRegion = calculateCropRegion(imageWidth, imageHeight, screenWidth, screenHeight);

        logger.info('autoCropDocument', 'Calculated crop region', cropRegion);

        // Perform crop on image as-is (no rotation)
        const result = await ImageManipulator.manipulateAsync(
            imageUri,
            [
                {
                    crop: {
                        originX: cropRegion.originX,
                        originY: cropRegion.originY,
                        width: cropRegion.width,
                        height: cropRegion.height,
                    },
                },
            ],
            {
                compress: 0.75, // Reduced from 0.85 to reduce file size before upload
                format: ImageManipulator.SaveFormat.JPEG,
                base64: false,
            }
        );

        logger.info('autoCropDocument', 'Crop completed', { uri: result.uri });

        return result.uri;
    } catch (error) {
        logger.error('autoCropDocument', 'Failed to crop image', error);
        // Return original URI as fallback
        return imageUri;
    }
}

/**
 * Get image dimensions from a URI
 * This is a helper function to get dimensions before cropping
 */
export async function getImageDimensions(uri: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const Image = require('react-native').Image;
        // Remove file:// prefix if present for Image.getSize
        const imageUri = uri.startsWith('file://') ? uri : `file://${uri}`;
        Image.getSize(
            imageUri,
            (width: number, height: number) => {
                resolve({ width, height });
            },
            (error: Error) => {
                logger.error('getImageDimensions', 'Failed to get image dimensions', error);
                reject(error);
            }
        );
    });
}

